import { useState, useRef, type ChangeEvent } from "react";
import { Loader, ImageIcon, X, Check, Sparkles, RotateCcw, Info } from "lucide-react";
import { usePhases, useStages } from "../hooks/useStageConfig";
import { useAuth } from "../providers/AuthProvider";
import { validateFiles } from "../utils/fileValidation";
import { useCreateReconRequest } from "../hooks/useVehicleReconRequests";
import { useAIPhotoDescription, isImageFile } from "../hooks/useAIPhotoDescription";
import { useAIReconSuggestions, type UploadedImage } from "../hooks/useAIReconSuggestions";
import { attachObject, createAiSummaryNote, presignUpload } from "../services/attachmentService";
import { buildAiSummaryNoteText } from "../utils/aiSummaryNote";
import { useQueryClient } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

interface Vehicle {
  vin: string;
  vehicle_inventory_id: string;
  vehicle_year?: string | number;
  make?: string;
  model?: string;
  vehicle?: string;
}

interface StageFormData {
  description: string;
  eta: string;
  requirement_type: string;
  showDescription?: boolean;
}

interface RequestResult {
  phase: string;
  stage: string;
  success: boolean;
  data?: unknown;
}

interface RequestError {
  phase: string;
  stage: string;
  error: string;
}

interface ReconRequestModalProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onSuccess?: (results: RequestResult[]) => void;
}

const ReconRequestModal = ({
  vehicle,
  open,
  onClose,
  onSuccess,
}: ReconRequestModalProps) => {
  const { user } = useAuth();
  const createReconRequest = useCreateReconRequest();
  const queryClient = useQueryClient();
  const { generateDescription } = useAIPhotoDescription();
  const { suggestReconRequests, isLoading: isAIFilling } = useAIReconSuggestions();
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, StageFormData>>({});
  const [stageFiles, setStageFiles] = useState<Record<string, File[]>>({});
  const [aiLoadingStages, setAiLoadingStages] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [aiImageFiles, setAiImageFiles] = useState<File[]>([]);
  // Upload-once: AI images are uploaded to R2 a single time; these keys are reused
  // when attaching on submit (no re-upload). Keyed per stage by the same order the
  // AI `image_indices` refer to.
  const [aiUploads, setAiUploads] = useState<UploadedImage[]>([]);
  const [stageImageKeys, setStageImageKeys] = useState<Record<string, string[]>>({});
  // Images whose upload to R2 failed during autofill — shown so the user can retry.
  const [aiFailedUploads, setAiFailedUploads] = useState<File[]>([]);
  const [retryingUploads, setRetryingUploads] = useState(false);
  // The last autofill returned no usable stages; its photos still go on a note.
  const [aiFoundNothing, setAiFoundNothing] = useState(false);
  const aiFileInputRef = useRef<HTMLInputElement>(null);

  const { data: phasesData = [] } = usePhases();
  const { data: allStages = [] } = useStages();

  const computeDefaultEta = (stageName: string): string => {
    const stageObj = allStages.find((s) => s.name === stageName);
    const days = stageObj?.expected_days ?? 1;
    const date = new Date();
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const requiresDescription = (stageName: string): boolean =>
    allStages.find((s) => s.name === stageName)?.requires_description ?? false;

  const handleStageToggle = (phase: string, stage: string) => {
    const stageKey = `${phase}|${stage}`;
    setSelectedStages((prev) => {
      const isSelected = prev.some((s) => s === stageKey);

      if (isSelected) {
        setFormData((prevData) => {
          const newData = { ...prevData };
          delete newData[stageKey];
          return newData;
        });
        setStageFiles((prevFiles) => {
          const newFiles = { ...prevFiles };
          delete newFiles[stageKey];
          return newFiles;
        });
        return prev.filter((s) => s !== stageKey);
      } else {
        const defaultEta = computeDefaultEta(stage);
        const descriptionRequired = requiresDescription(stage);
        setFormData((prevData) => ({
          ...prevData,
          [stageKey]: {
            description: "",
            eta: defaultEta,
            requirement_type: "service",
            showDescription: descriptionRequired,
          },
        }));
        setStageFiles((prevFiles) => ({
          ...prevFiles,
          [stageKey]: [],
        }));
        return [...prev, stageKey];
      }
    });
  };

  const handleFormChange = (
    stageKey: string,
    field: keyof StageFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [stageKey]: {
        ...prev[stageKey],
        [field]: value,
      },
    }));
  };

  const handleGenerateDescription = async (stageKey: string, imageFiles: File[]) => {
    if (imageFiles.length === 0) return;

    setFormData((prev) => ({
      ...prev,
      [stageKey]: { ...prev[stageKey], showDescription: true },
    }));
    setAiLoadingStages((prev) => ({ ...prev, [stageKey]: true }));

    try {
      const description = await generateDescription(imageFiles);
      setFormData((prev) => ({
        ...prev,
        [stageKey]: { ...prev[stageKey], description, showDescription: true },
      }));
    } catch {
      toast.error("Could not generate description. Please write it manually.");
    } finally {
      setAiLoadingStages((prev) => ({ ...prev, [stageKey]: false }));
    }
  };

  const handleFileSelect = (
    stageKey: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const { validFiles, invalidFiles } = validateFiles(files);

    if (invalidFiles.length > 0) {
      const errors = invalidFiles
        .map((f) => `${f.file.name}: ${f.errors.join(", ")}`)
        .join(", ");
      toast.error("Some files could not be added.", { description: errors });
    }

    if (validFiles.length > 0) {
      const newFiles = [...(stageFiles[stageKey] || []), ...validFiles];
      setStageFiles((prev) => ({ ...prev, [stageKey]: newFiles }));
    }

    event.target.value = "";
  };

  const handleRemoveFile = (stageKey: string, fileIndex: number) => {
    setStageFiles((prev) => ({
      ...prev,
      [stageKey]: prev[stageKey].filter((_, idx) => idx !== fileIndex),
    }));
  };

  const resetForm = () => {
    setSelectedStages([]);
    setFormData({});
    setStageFiles({});
    setAiLoadingStages({});
    setSubmitting(false);
    setAiImageFiles([]);
    setAiUploads([]);
    setStageImageKeys({});
    setAiFailedUploads([]);
    setRetryingUploads(false);
    setAiFoundNothing(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAIAutoFill = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const { validFiles, invalidFiles } = validateFiles(files);
    if (invalidFiles.length > 0) {
      const errors = invalidFiles
        .map((f) => `${f.file.name}: ${f.errors.join(", ")}`)
        .join(", ");
      toast.error("Some files could not be added.", { description: errors });
    }

    const imageFiles = validFiles.filter(isImageFile);
    if (imageFiles.length === 0) {
      toast.error("Please upload at least one image for AI analysis.");
      return;
    }

    setAiImageFiles(imageFiles);
    setAiFoundNothing(false);

    try {
      const result = await suggestReconRequests(imageFiles);
      const suggestions = result.suggestions ?? [];

      const newSelectedStages: string[] = [];
      const newFormData: Record<string, StageFormData> = {};
      const newStageFiles: Record<string, File[]> = {};
      const newStageImageKeys: Record<string, string[]> = {};

      for (const suggestion of suggestions) {
        const phaseExists = phasesData.some((p) => p.name === suggestion.phase);
        const stageExists = allStages.some((s) => s.name === suggestion.stage);
        if (!phaseExists || !stageExists) {
          continue;
        }

        const stageKey = `${suggestion.phase}|${suggestion.stage}`;
        newSelectedStages.push(stageKey);

        const etaDate = new Date();
        etaDate.setDate(etaDate.getDate() + suggestion.estimated_days);
        const year = etaDate.getFullYear();
        const month = String(etaDate.getMonth() + 1).padStart(2, "0");
        const day = String(etaDate.getDate()).padStart(2, "0");
        const eta = `${year}-${month}-${day}`;

        newFormData[stageKey] = {
          description: suggestion.description,
          eta,
          requirement_type: "service",
          showDescription: !!suggestion.description || requiresDescription(suggestion.stage),
        };

        const validIdx = suggestion.image_indices.filter(
          (idx: number) => idx >= 0 && idx < imageFiles.length && idx < result.uploads.length
        );
        const stageImages = validIdx.map((idx: number) => imageFiles[idx]);
        const stageKeys = validIdx.map((idx: number) => result.uploads[idx].objectKey);
        if (stageImages.length > 0) {
          newStageFiles[stageKey] = stageImages;
          newStageImageKeys[stageKey] = stageKeys;
        }
      }

      setAiUploads(result.uploads);
      setAiFailedUploads(result.failedUploads);
      if (result.failedUploads.length > 0) {
        toast.warning(
          `${result.failedUploads.length} image(s) didn't upload — you can retry them below.`
        );
      }

      if (newSelectedStages.length === 0) {
        // Keep the uploads (and any stages the user already picked): the photos are
        // saved to a note via "Save photos to notes" or alongside manual requests.
        setAiFoundNothing(true);
        return;
      }

      setSelectedStages(newSelectedStages);
      setFormData(newFormData);
      setStageFiles(newStageFiles);
      setStageImageKeys(newStageImageKeys);

      toast.success(
        `AI suggested ${newSelectedStages.length} recon stage(s).`,
        { description: "Review and adjust the details below before submitting." }
      );
    } catch {
      setAiImageFiles([]);
      setAiUploads([]);
      setStageImageKeys({});
      setAiFailedUploads([]);
      toast.error("AI auto-fill failed. Please fill the form manually.");
    }
  };

  // Re-upload the images that failed during autofill (re-sends the same files, no
  // re-pick). Successful ones join aiUploads so they're attached to the summary note.
  const handleRetryFailedAiUploads = async () => {
    if (aiFailedUploads.length === 0 || retryingUploads) return;
    const retrying = aiFailedUploads;
    setRetryingUploads(true);
    try {
      const results = await Promise.all(retrying.map((f) => presignUpload(f)));
      const recovered: UploadedImage[] = [];
      const stillFailed: File[] = [];
      results.forEach((res, i) => {
        if (res.success && res.object_key) {
          recovered.push({ file: retrying[i], objectKey: res.object_key });
        } else {
          stillFailed.push(retrying[i]);
        }
      });
      if (recovered.length > 0) {
        setAiUploads((prev) => [...prev, ...recovered]);
      }
      setAiFailedUploads(stillFailed);
      if (stillFailed.length > 0) {
        toast.error(`${stillFailed.length} image(s) still failed to upload.`);
      } else {
        toast.success("All images uploaded.");
      }
    } finally {
      setRetryingUploads(false);
    }
  };

  // Create the AI summary note with every AI photo attached. The text always
  // records the AI result, so the photos are kept even with no stage descriptions.
  const saveAiSummaryNote = (stages: { stage: string; description?: string }[]) =>
    createAiSummaryNote({
      vehicleInventoryId: vehicle.vehicle_inventory_id,
      summaryText: buildAiSummaryNoteText({
        photoCount: aiUploads.length,
        aiFoundNothing,
        stages,
      }),
      userEmail: user?.email || "unknown@user.com",
      images: aiUploads.map((u) => ({
        object_key: u.objectKey,
        file_name: u.file.name,
        file_size: u.file.size,
        mime_type: u.file.type || "image/jpeg",
      })),
    });

  const refreshNoteQueries = (vid: string, noteId?: string) => {
    queryClient.invalidateQueries({ queryKey: ["vehicle-notes", vid] });
    if (noteId) {
      queryClient.invalidateQueries({ queryKey: ["entity-attachments", "note", noteId] });
    }
  };

  // AI photos but no stages selected: the photo note is the user's main action, so
  // await it, and keep the modal open on failure so the photos aren't lost.
  const handleSavePhotosOnly = async () => {
    setSubmitting(true);
    try {
      const res = await saveAiSummaryNote([]);
      refreshNoteQueries(vehicle.vehicle_inventory_id, res?.note?.note_id);
      toast.success("Photos saved to vehicle notes.");
      handleClose();
    } catch {
      toast.error("Photos could not be saved to notes. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    const missingFields = selectedStages.filter((stageKey) => {
      const [, stage] = stageKey.split("|");
      const data = formData[stageKey];

      if (requiresDescription(stage) && !data?.description?.trim()) {
        return true;
      }
      if (!data?.eta) {
        return true;
      }
      return false;
    });

    if (missingFields.length > 0) {
      const missingNames = missingFields.map((key) => {
        const [phase, stage] = key.split("|");
        return `${phase} - ${stage}`;
      });
      toast.error("Please fill in all required fields.", {
        description: missingNames.join(", "),
      });
      return;
    }

    if (selectedStages.length === 0) {
      toast.error("Please select at least one recon stage.");
      return;
    }

    setSubmitting(true);
    const results: RequestResult[] = [];
    const errors: RequestError[] = [];

    try {
      for (const stageKey of selectedStages) {
        const [phase, stage] = stageKey.split("|");
        const data = formData[stageKey];
        const files = stageFiles[stageKey] || [];
        const keys = stageImageKeys[stageKey] || [];
        // Pure-autofill stage: images are already in R2 -> attach by key (no
        // re-upload). Any manual edit that desyncs counts falls back to the
        // existing upload path so nothing is lost.
        const useKeys = keys.length > 0 && keys.length === files.length;

        try {
          const response = await createReconRequest.mutateAsync({
            vehicle_inventory_id: vehicle.vehicle_inventory_id,
            user: user?.email || "unknown@user.com",
            description: data.description || "",
            phase: phase,
            stage: stage,
            requirement_type: data.requirement_type || "service",
            eta: data.eta,
            files: useKeys || files.length === 0 ? undefined : files,
          });

          const requestId = (response as { request_id?: string })?.request_id;
          if (useKeys && requestId) {
            await Promise.all(
              keys.map((objectKey, i) =>
                attachObject({
                  objectKey,
                  entityType: "recon_request",
                  entityId: requestId,
                  file: files[i],
                  userEmail: user?.email || "unknown@user.com",
                })
              )
            );
          }

          console.log(
            `Created recon request for ${phase} - ${stage}:`,
            response
          );
          results.push({ phase, stage, success: true, data: response });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : typeof error === "object" &&
                  error !== null &&
                  "response" in error
                ? (error as { response?: { data?: { detail?: string } } })
                    .response?.data?.detail || "Request failed"
                : "Request failed";
          errors.push({
            phase,
            stage,
            error: errorMessage,
          });
        }
      }

      if (results.length > 0) {
        toast.success(`Successfully created ${results.length} recon request(s).`, {
          description: results.map((r) => `${r.phase} - ${r.stage}`).join(", "),
        });

        if (errors.length > 0) {
          toast.error(`Failed to create ${errors.length} recon request(s).`, {
            description: errors.map((e) => `${e.phase} - ${e.stage}: ${e.error}`).join(", "),
          });
        }

        if (onSuccess) onSuccess(results);

        if (aiUploads.length > 0) {
          // Decoupled from approval: fire the summary note WITHOUT awaiting, so the
          // modal closes immediately. The backend creates the note + attaches the
          // already-uploaded images (reusing variants); we refresh the Notes queries
          // when it returns. A failure here can never fail the approved requests.
          const vid = vehicle.vehicle_inventory_id;
          void saveAiSummaryNote(
            selectedStages.map((stageKey) => ({
              stage: stageKey.split("|")[1],
              description: formData[stageKey]?.description,
            }))
          )
            .then((res) => refreshNoteQueries(vid, res?.note?.note_id))
            .catch(() => {
              toast.error("Summary note could not be created.");
            });
        }

        handleClose();
      } else {
        toast.error("Failed to create all recon requests.", {
          description: errors.map((e) => `${e.phase} - ${e.stage}: ${e.error}`).join(", "),
        });
      }
    } catch {
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // With AI photos and nothing selected, the footer saves the photos to a note instead.
  const savePhotosOnly = aiUploads.length > 0 && selectedStages.length === 0;

  const renderStageFields = (stageKey: string, stage: string) => {
    const descriptionRequired = requiresDescription(stage);
    const showDescription = formData[stageKey]?.showDescription ?? descriptionRequired;
    const isGeneratingStageDescription = !!aiLoadingStages[stageKey];

    return (
      <CardContent className="space-y-4">
        {/* Description field — expanded or collapsed toggle */}
        <div className="space-y-2">
          {!showDescription ? (
            <button
              type="button"
              onClick={() => handleFormChange(stageKey, "showDescription", true)}
              className="w-full p-3 border-2 border-dashed border-muted-foreground/30 rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors text-center"
            >
              + Add description (optional)
            </button>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor={`${stageKey}-description`}>
                  Description{" "}
                  {descriptionRequired && (
                    <span className="text-destructive">*</span>
                  )}
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleGenerateDescription(
                      stageKey,
                      stageFiles[stageKey]?.filter(isImageFile) ?? []
                    )
                  }
                  disabled={
                    (stageFiles[stageKey]?.filter(isImageFile).length ?? 0) === 0 ||
                    isGeneratingStageDescription
                  }
                  className="h-7 gap-1.5 text-xs"
                >
                  {isGeneratingStageDescription ? (
                    <Loader className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {isGeneratingStageDescription ? "Generating…" : "AI Description"}
                </Button>
              </div>
              <Textarea
                id={`${stageKey}-description`}
                value={formData[stageKey]?.description || ""}
                onChange={(e) =>
                  handleFormChange(stageKey, "description", e.target.value)
                }
                placeholder={
                  isGeneratingStageDescription
                    ? "AI is analyzing your photos…"
                    : `Describe the work needed for ${stage}...`
                }
                rows={3}
              />
            </>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${stageKey}-eta`}>
            Expected Timeline <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${stageKey}-eta`}
            type="date"
            value={formData[stageKey]?.eta || ""}
            onChange={(e) =>
              handleFormChange(stageKey, "eta", e.target.value)
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${stageKey}-files`}>
            <div className="flex items-center gap-2">
              <ImageIcon size={16} className="text-muted-foreground" />
              Photos (Optional)
            </div>
          </Label>
          <Input
            id={`${stageKey}-files`}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,video/mp4,video/quicktime,video/webm,video/x-msvideo,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,.jpg,.jpeg,.png,.webp,.heic,.mp4,.mov,.webm,.avi,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            multiple
            onChange={(e) => handleFileSelect(stageKey, e)}
            className="cursor-pointer"
          />
          <p className="text-xs text-muted-foreground">
            Max 100MB per file • Images, Videos, Documents
          </p>

          {stageFiles[stageKey] && stageFiles[stageKey].length > 0 && (
            <div className="mt-2 space-y-1">
              {stageFiles[stageKey].map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2 bg-muted rounded text-sm"
                >
                  <span className="truncate flex-1">{file.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFile(stageKey, idx)}
                    className="h-6 w-6 p-0 ml-2"
                  >
                    <X size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Recon Requests</DialogTitle>
          <DialogDescription>
            VIN: {vehicle.vin} • {vehicle.vehicle_year} {vehicle.make}{" "}
            {vehicle.model}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea
          className="flex-1 pr-4 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 200px)" }}
        >
          {isAIFilling && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="relative">
                <Sparkles size={32} className="text-primary animate-pulse" />
              </div>
              <div className="text-center space-y-2">
                <p className="text-base font-medium">
                  AI is analyzing {aiImageFiles.length} photo{aiImageFiles.length !== 1 ? "s" : ""}...
                </p>
                <p className="text-sm text-muted-foreground">
                  Detecting stages, writing descriptions, and estimating timelines.
                  <br />
                  This may take a moment.
                </p>
              </div>
              <Loader size={20} className="animate-spin text-muted-foreground" />
            </div>
          )}

          <div className={`mb-6 ${isAIFilling ? "hidden" : ""}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Select Recon Stages</h3>
              <div>
                <input
                  ref={aiFileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,.jpg,.jpeg,.png,.webp,.heic"
                  multiple
                  className="hidden"
                  onChange={handleAIAutoFill}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isAIFilling || submitting}
                  onClick={() => aiFileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Sparkles size={16} />
                  Auto-fill with AI
                </Button>
              </div>
            </div>

            {aiFoundNothing && aiUploads.length > 0 && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <Info size={16} className="mt-0.5 flex-shrink-0 text-primary" />
                <p className="text-sm">
                  AI reviewed {aiUploads.length} photo{aiUploads.length !== 1 ? "s" : ""} and
                  found no recon work.{" "}
                  {selectedStages.length === 0
                    ? "Pick stages manually or save the photos to notes."
                    : "The photos will be saved to a note with your requests."}
                </p>
              </div>
            )}

            {aiFailedUploads.length > 0 && (
              <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-destructive">
                    {aiFailedUploads.length} image{aiFailedUploads.length !== 1 ? "s" : ""} failed
                    to upload
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={retryingUploads}
                    onClick={handleRetryFailedAiUploads}
                    className="gap-1.5"
                  >
                    <RotateCcw size={14} className={retryingUploads ? "animate-spin" : ""} />
                    {retryingUploads ? "Retrying…" : "Retry upload"}
                  </Button>
                </div>
                <ul className="mt-2 space-y-1">
                  {aiFailedUploads.map((file, i) => (
                    <li
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <ImageIcon size={12} className="flex-shrink-0" />
                      <span className="truncate" title={file.name}>
                        {file.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-4">
              {phasesData.map((phase) => {
                const phaseStages = allStages
                  .filter((s) => s.phase_name === phase.name)
                  .sort((a, b) => a.sort_order - b.sort_order);

                return (
                  <div key={phase.id}>
                    <div className="mb-2">
                      <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        {phase.name}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 pl-2 pb-2 border-l-2 border-border">
                      {phaseStages.map((stageObj) => {
                        const stageKey = `${phase.name}|${stageObj.name}`;
                        const isSelected = selectedStages.includes(stageKey);

                        return (
                          <button
                            key={stageObj.id}
                            type="button"
                            onClick={() => handleStageToggle(phase.name, stageObj.name)}
                            className={`
                              inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                              transition-all duration-150 ease-in-out
                              border-2 min-h-11
                              ${
                                isSelected
                                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                  : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted"
                              }
                            `}
                          >
                            {isSelected && (
                              <Check size={16} className="flex-shrink-0" />
                            )}
                            {stageObj.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedStages.length > 0 && !isAIFilling && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Request Details</h3>

              {selectedStages.map((stageKey) => {
                const [phase, stage] = stageKey.split("|");

                return (
                  <Card key={stageKey}>
                    <CardHeader>
                      <CardTitle className="text-base">
                        {phase} - {stage}
                      </CardTitle>
                    </CardHeader>
                    {renderStageFields(stageKey, stage)}
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {selectedStages.length > 0
              ? `${selectedStages.length} stage(s) selected`
              : "No stages selected"}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting || isAIFilling}
            >
              Cancel
            </Button>
            <Button
              onClick={savePhotosOnly ? handleSavePhotosOnly : handleSubmit}
              disabled={
                (selectedStages.length === 0 && !savePhotosOnly) || submitting || isAIFilling
              }
            >
              {submitting ? (
                <>
                  <Loader size={16} className="animate-spin mr-2" />
                  {savePhotosOnly ? "Saving Photos..." : "Creating Requests..."}
                </>
              ) : savePhotosOnly ? (
                "Save photos to notes"
              ) : (
                "Create Requests"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconRequestModal;
